import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useLang } from "@/contexts/LangContext";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Phone, Video, Mail, CheckSquare, FileText, Trash2, ListChecks } from "lucide-react";
import { canDeleteRecord } from "@/lib/perms";
import { toast } from "sonner";

const TYPES = [
  { v: "call", icon: Phone },
  { v: "meeting", icon: Video },
  { v: "email", icon: Mail },
  { v: "task", icon: CheckSquare },
  { v: "note", icon: FileText },
];

const TYPE_ICONS = Object.fromEntries(TYPES.map((t) => [t.v, t.icon]));

const emptyForm = { type: "task", title: "", note: "", client_id: "", deal_id: "", due_date: "", completed: false };

const Activities = () => {
  const { t } = useLang();
  const { can, user } = useAuth();
  const canWrite = can("activities:write");
  const [items, setItems] = useState([]);
  const [clients, setClients] = useState([]);
  const [deals, setDeals] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [filter, setFilter] = useState("all");

  const load = useCallback(async () => {
    const [a, c, d] = await Promise.all([api.get("/activities"), api.get("/clients"), api.get("/deals")]);
    setItems(a.data);
    setClients(c.data);
    setDeals(d.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!form.title) { toast.error(t("title")); return; }
    const payload = { ...form };
    if (!payload.client_id) payload.client_id = null;
    if (!payload.deal_id) payload.deal_id = null;
    if (!payload.due_date) payload.due_date = null;
    await api.post("/activities", payload);
    toast.success(t("save"));
    setOpen(false);
    setForm(emptyForm);
    await load();
  };

  const toggle = async (a) => {
    await api.put(`/activities/${a.id}`, { completed: !a.completed });
    await load();
  };

  const del = async (id) => {
    if (!window.confirm("Hapus?")) return;
    try {
      await api.delete(`/activities/${id}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  const filtered = filter === "all" ? items : items.filter((a) => a.type === filter);
  const clientMap = Object.fromEntries(clients.map((c) => [c.id, c]));

  const now = new Date();
  const isOverdue = (a) => a.due_date && !a.completed && new Date(a.due_date) < now;

  return (
    <div className="space-y-6" data-testid="activities-notes-view">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>{t("activities")}</h2>
          <p className="text-sm text-slate-500 mt-1">{items.length}</p>
        </div>
        <div className="flex gap-2">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger data-testid="activity-filter-type" className="w-40"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("all")}</SelectItem>
              {TYPES.map((tt) => <SelectItem key={tt.v} value={tt.v}>{t(tt.v)}</SelectItem>)}
            </SelectContent>
          </Select>
          <Dialog open={open} onOpenChange={setOpen}>
            {canWrite && (
              <DialogTrigger asChild>
                <Button data-testid="add-activity-button" className="bg-orange-500 hover:bg-orange-600 gap-2">
                  <Plus className="w-4 h-4" /> {t("add_activity")}
                </Button>
              </DialogTrigger>
            )}
            <DialogContent className="max-w-lg">
              <DialogHeader><DialogTitle>{t("new_activity")}</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">{t("type")}</Label>
                  <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                    <SelectTrigger data-testid="activity-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {TYPES.map((tt) => <SelectItem key={tt.v} value={tt.v}>{t(tt.v)}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">{t("due_date")}</Label>
                  <Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} />
                </div>
                <div className="col-span-2">
                  <Label className="text-xs">{t("title")}</Label>
                  <Input data-testid="activity-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
                </div>
                <div>
                  <Label className="text-xs">{t("client")}</Label>
                  <Select value={form.client_id || "none"} onValueChange={(v) => setForm({ ...form, client_id: v === "none" ? "" : v })}>
                    <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">—</SelectItem>
                      {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.company}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Deal</Label>
                  <Select value={form.deal_id || "none"} onValueChange={(v) => setForm({ ...form, deal_id: v === "none" ? "" : v })}>
                    <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">—</SelectItem>
                      {deals.map((d) => <SelectItem key={d.id} value={d.id}>{d.title}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2">
                  <Label className="text-xs">{t("notes")}</Label>
                  <Textarea rows={3} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>{t("cancel")}</Button>
                <Button onClick={submit} data-testid="save-activity-button" className="bg-orange-500 hover:bg-orange-600">{t("save")}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {filtered.length === 0 ? (
        <Card className="p-12 text-center text-slate-400 border-dashed">
          <ListChecks className="w-10 h-10 mx-auto mb-3 text-slate-300" />
          <p className="text-sm">{t("empty_activities")}</p>
        </Card>
      ) : (
        <Card className="border-slate-200 shadow-sm divide-y divide-slate-100">
          {filtered.map((a) => {
            const Icon = TYPE_ICONS[a.type] || FileText;
            const overdue = isOverdue(a);
            return (
              <div key={a.id} className="flex items-start gap-3 p-4 hover:bg-slate-50/70" data-testid={`activity-item-${a.id}`}>
                <Checkbox checked={a.completed} disabled={!canWrite} onCheckedChange={() => toggle(a)} className="mt-1" />
                <div className={`w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0 ${a.completed ? "opacity-50" : ""}`}>
                  <Icon className="w-4 h-4 text-slate-700" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-sm font-medium ${a.completed ? "line-through text-slate-400" : "text-slate-900"}`}>{a.title}</span>
                    <span className="text-[10px] uppercase font-semibold tracking-wider px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">{t(a.type)}</span>
                    {overdue && <span className="text-[10px] uppercase font-semibold tracking-wider px-1.5 py-0.5 rounded bg-rose-50 text-rose-700">{t("overdue")}</span>}
                  </div>
                  {a.note && <p className="text-xs text-slate-500 mt-1">{a.note}</p>}
                  <div className="text-[11px] text-slate-400 mt-1 flex gap-3">
                    {a.due_date && <span>📅 {a.due_date}</span>}
                    {a.client_id && clientMap[a.client_id] && <span>{clientMap[a.client_id].company}</span>}
                    {a.created_by_name && <span>{t("created_by")}: {a.created_by_name}</span>}
                  </div>
                </div>
                {canDeleteRecord(user, a, "created_by") && (
                  <button onClick={() => del(a.id)} className="p-1.5 text-slate-400 hover:text-rose-600" data-testid={`delete-activity-${a.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            );
          })}
        </Card>
      )}
    </div>
  );
};

export default Activities;
